import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { useAppDispatch, useAppSelector } from '@/store'
import {
  confirmRequirement,
  rejectRequirement,
  requirementsErrorDismissed,
  selectRequirementsError,
  selectRequirementsMutating,
  supersedeRequirement
} from '../slices/requirementsSlice'
import type { RequirementRow, RequirementSupersedeInput } from '../types'

/**
 * AC-4/AC-5/Decision 6/7 — the three real verbs that replace the fixture's
 * one fake "approve" act: confirm / reject (a required, non-blank reason)
 * / supersede (an inline form surfacing its two genuinely-optional fields,
 * deferred finding c1-8 — only non-blank trimmed values are sent, so an
 * all-blank submit still posts `{}`). Each posts to its OWN endpoint and
 * carries the caller's identity from the session — there is no "act as"
 * control. A refusal renders INLINE, scoped by this row's `ref` (a lede plus
 * the status and code in their own elements, deferred finding c2-3 client
 * half — the server's message IS the bare key, so a human sentence is a
 * server change out of scope here), and the row stays exactly as it was
 * before the attempt (INV-2.4).
 *
 * The UI hides an action the row's STATUS makes structurally impossible
 * (PROPOSED → confirm/reject, CONFIRMED → supersede, terminal → none); it
 * never pre-empts a server refusal for any other reason (§G.9).
 */
export function RequirementActions({
  orgId,
  projectId,
  row
}: {
  orgId: string
  projectId: string
  row: RequirementRow
}) {
  const { t } = useTranslation('projectWorkspace')
  const { t: tCommon } = useTranslation('common')
  const dispatch = useAppDispatch()
  const mutating = useAppSelector(selectRequirementsMutating)
  const error = useAppSelector(selectRequirementsError)
  const [rejecting, setRejecting] = useState(false)
  const [reason, setReason] = useState('')
  const [reasonTouched, setReasonTouched] = useState(false)
  const [superseding, setSuperseding] = useState(false)
  const [supersedingRef, setSupersedingRef] = useState('')
  const [supersedeReason, setSupersedeReason] = useState('')

  const busy = mutating === row.ref
  const rowError = error?.scope === row.ref ? error : null
  const reasonBlank = reason.trim() === ''

  const onConfirmClicked = (): void => {
    void dispatch(confirmRequirement({ orgId, projectId, ref: row.ref }))
  }

  const onRejectToggled = (): void => {
    dispatch(requirementsErrorDismissed(row.ref))
    setRejecting((open) => !open)
    setReasonTouched(false)
  }

  const onRejectSubmitted = (): void => {
    setReasonTouched(true)
    if (reasonBlank) return
    void dispatch(rejectRequirement({ orgId, projectId, ref: row.ref, body: { reason: reason.trim() } })).then(
      (action) => {
        if (rejectRequirement.fulfilled.match(action)) {
          setRejecting(false)
          setReason('')
        }
      }
    )
  }

  const onSupersedeToggled = (): void => {
    dispatch(requirementsErrorDismissed(row.ref))
    setSuperseding((open) => !open)
  }

  // Deferred finding c1-8 (TASK-71426B1C): `supersedingRequirementRef` and
  // `reason` are both genuinely optional on the endpoint — only the
  // non-blank, trimmed fields are sent, so an all-blank form still posts
  // `{}` exactly as before.
  const onSupersedeSubmitted = (): void => {
    const body: RequirementSupersedeInput = {}
    const trimmedRef = supersedingRef.trim()
    const trimmedReason = supersedeReason.trim()
    if (trimmedRef !== '') body.supersedingRequirementRef = trimmedRef
    if (trimmedReason !== '') body.reason = trimmedReason
    void dispatch(supersedeRequirement({ orgId, projectId, ref: row.ref, body })).then((action) => {
      if (supersedeRequirement.fulfilled.match(action)) {
        setSuperseding(false)
        setSupersedingRef('')
        setSupersedeReason('')
      }
    })
  }

  return (
    <div className="flex flex-col items-start gap-1.5">
      <div className="flex flex-wrap gap-1.5">
        {row.status === 'PROPOSED' && (
          <>
            <Button size="sm" disabled={busy} onClick={onConfirmClicked} data-testid={`confirm-${row.ref}`}>
              {t('requirements.actions.confirm')}
            </Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={onRejectToggled} data-testid={`reject-${row.ref}`}>
              {t('requirements.actions.reject')}
            </Button>
          </>
        )}
        {row.status === 'CONFIRMED' && (
          <Button size="sm" variant="outline" disabled={busy} onClick={onSupersedeToggled} data-testid={`supersede-${row.ref}`}>
            {t('requirements.actions.supersede')}
          </Button>
        )}
      </div>

      {superseding && (
        <div className="flex w-full max-w-xs flex-col gap-1">
          <label htmlFor={`supersede-ref-${row.ref}`} className="text-xs font-medium text-foreground">
            {t('requirements.actions.supersedeRefLabel')}
          </label>
          <input
            id={`supersede-ref-${row.ref}`}
            value={supersedingRef}
            onChange={(event) => setSupersedingRef(event.target.value)}
            className="h-8 rounded-md border border-input bg-card px-2 text-[13px] text-foreground"
          />
          <label htmlFor={`supersede-reason-${row.ref}`} className="text-xs font-medium text-foreground">
            {t('requirements.actions.supersedeReasonLabel')}
          </label>
          <textarea
            id={`supersede-reason-${row.ref}`}
            value={supersedeReason}
            onChange={(event) => setSupersedeReason(event.target.value)}
            className="rounded-md border border-input bg-card px-2 py-1 text-[13px] text-foreground"
            rows={2}
          />
          <div className="flex gap-1.5">
            <Button size="sm" disabled={busy} onClick={onSupersedeSubmitted} data-testid={`supersede-submit-${row.ref}`}>
              {t('requirements.actions.supersedeSubmit')}
            </Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={onSupersedeToggled}>
              {t('requirements.actions.cancel')}
            </Button>
          </div>
        </div>
      )}

      {rejecting && (
        <div className="flex w-full max-w-xs flex-col gap-1">
          <label htmlFor={`reject-reason-${row.ref}`} className="text-xs font-medium text-foreground">
            {t('requirements.actions.rejectReasonLabel')}
          </label>
          <textarea
            id={`reject-reason-${row.ref}`}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            aria-invalid={reasonTouched && reasonBlank}
            aria-describedby={reasonTouched && reasonBlank ? `reject-reason-error-${row.ref}` : undefined}
            className="rounded-md border border-input bg-card px-2 py-1 text-[13px] text-foreground"
            rows={2}
          />
          {reasonTouched && reasonBlank && (
            <p id={`reject-reason-error-${row.ref}`} className="text-xs text-blocked" role="alert">
              {t('requirements.actions.rejectReasonRequired')}
            </p>
          )}
          <div className="flex gap-1.5">
            <Button size="sm" disabled={busy} onClick={onRejectSubmitted}>
              {t('requirements.actions.rejectSubmit')}
            </Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={onRejectToggled}>
              {t('requirements.actions.cancel')}
            </Button>
          </div>
        </div>
      )}

      {rowError !== null && (
        // `whitespace-normal`: the row cell (shadcn TableCell) is
        // `whitespace-nowrap`, which would keep `break-all` below from ever
        // breaking a long code; `max-w-xs` caps it like the sibling forms
        // (TASK-A274D065 fix cycle 1 — it overflowed the card by ~225px).
        <div
          role="alert"
          className="flex w-full max-w-xs flex-col gap-0.5 whitespace-normal text-xs text-blocked"
          data-testid={`requirement-error-${row.ref}`}
        >
          <span>{t('requirements.actions.refusedLede')}</span>
          <dl className="mt-0.5 flex flex-wrap gap-x-4">
            {rowError.status > 0 && (
              <div className="flex items-center gap-1">
                <dt className="text-muted-foreground">{tCommon('states.statusLabel')}</dt>
                <dd data-testid={`requirement-error-status-${row.ref}`} className="text-foreground">
                  {rowError.status}
                </dd>
              </div>
            )}
            {rowError.code !== undefined && (
              <div className="flex items-center gap-1">
                <dt className="text-muted-foreground">{tCommon('states.codeLabel')}</dt>
                <dd
                  data-testid={`requirement-error-code-${row.ref}`}
                  className="min-w-0 break-all font-mono text-foreground"
                >
                  {rowError.code}
                </dd>
              </div>
            )}
          </dl>
          {rowError.code === undefined && (
            <p data-testid={`requirement-error-code-${row.ref}`} className="min-w-0 break-all text-foreground">
              {rowError.message}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
